import { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import axios, { AxiosError } from 'axios';
import { useLogoutContext } from '../providers/logout-context';

// Why the server signed this user out, shown once on the login page.
export const SIGNED_OUT_REASON_KEY = 'fa:signedOutReason';

type Config = {
    url: string;
    method: string;
    body?: any;
    alwaysAllowed?: boolean;
} | null;

type Result<T = any> = {
    data: T | null;
    error: AxiosError | null | string;
    isLoading: boolean;
    // The raw error response body, when the backend sent structured JSON
    // beyond just a message - e.g. { action: 'upgrade_required', toTierId }
    // from require-subscription-feature.js. `error` above stays a plain
    // string for every existing call site that only ever read that; this is
    // additive, for a call site that needs more than the message.
    errorData: any;
};

const useApi = <T = any>(): [Result<T>, React.Dispatch<React.SetStateAction<Config>>] => {
    const [config, setConfig] = useState<Config>(null);
    const [result, setResult] = useState<Result<T>>({ data: null, error: null, errorData: null, isLoading: false });
    const { isLoggingOut } = useLogoutContext();
    const abortControllerRef = useRef<AbortController | null>(null);
    const isLoggingOutRef = useRef(isLoggingOut);

    const token = Cookies.get('token');

    useEffect(() => {
        isLoggingOutRef.current = isLoggingOut;
    }, [isLoggingOut]);

    useEffect(() => {
        if (!config) return;

        // If we're logging out, cancel any pending requests
        // unless the request is marked as alwaysAllowed
        if (isLoggingOutRef.current && !config.alwaysAllowed) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            return;
        }

        // Abort any still-in-flight request from a previous config change
        // before starting this one - otherwise, if this hook instance's
        // setFetch gets called again before the prior request resolves (e.g.
        // an effect re-firing because some other dependency, like the user
        // object reference, changed again shortly after), both requests run
        // concurrently with nothing stopping the older one from resolving
        // AFTER the newer one and overwriting it with stale data. That's a
        // pure network-timing race, so it doesn't reproduce every time -
        // exactly the "intermittent, needs a second reload" shape of bug.
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const { url, method, body } = config;
        let headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        setResult({ data: null, error: null, errorData: null, isLoading: true });
        
        // Create a new AbortController for this request
        abortControllerRef.current = new AbortController();

        axios({
            method,
            url,
            headers,
            data: body ?? {},
            signal: abortControllerRef.current.signal,
        })
        .then((response) => {
            setResult({ data: response.data, error: null, errorData: null, isLoading: false });
        })
        .catch((error: AxiosError | any) => {
            // Don't surface an error for a request we cancelled ourselves -
            // either during logout, or because it was superseded by a newer
            // config change above. Either way there's nothing to show; the
            // request that matters is still in flight (or already resolved).
            if (axios.isCancel(error)) {
                return;
            }
            // 401 from tokenValidator (middlewares/token-validator.js) means
            // the session itself is gone - no token, an unrecognized user, or
            // (most commonly) an expired JWT. Only treat it as a session
            // expiry when a token was actually sent though - a bare 401 from
            // the login form itself (wrong email/password, user-controller.js)
            // never carries one, and must NOT bounce the user off the login
            // page they're already on. 403 is a different signal entirely
            // (require-permission.js/require-master.js - authenticated fine,
            // just not allowed to do this one thing) and must never force a
            // logout either.
            if (error.response?.status === 401 && token) {
                // The server ended the session (e.g. an admin changed this
                // user's access) - the login page says why.
                const body = error.response?.data as any;
                if (body?.code === 'session_expired' && body?.error) {
                    try { localStorage.setItem(SIGNED_OUT_REASON_KEY, body.error); } catch { /* best-effort message only */ }
                }
                Cookies.remove('token');
                Cookies.remove('user');
                Cookies.remove('entities');
                Cookies.remove('selectedCompany');
                window.location.href = '/login';
                return;
            }
            // Different controllers key their error body differently - most use
            // `error`, but plenty (e.g. provision-template-controller.js) use
            // `message` instead. Falling through only to error.message (axios's
            // own generic "Request failed with status code 400") silently
            // discarded that backend-provided detail and showed a bare status
            // code instead of the actual reason.
            const responseData = error.response?.data as any;
            // A plan gap (403 upgrade_required / addon_required) has already
            // opened the upgrade prompt - util/plan-required.ts.
            setResult({ data: null, error: responseData?.error ?? responseData?.message ?? error.message ?? error, errorData: responseData ?? null, isLoading: false });
        });
    }, [config, token]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return [result, setConfig];
};

export default useApi;
